import React, { useEffect, useState } from "react";
import {
    Heading,
    VStack,
    HStack,
    Button,
    Box,
    Text,
    Badge,
    useToast,
    Spinner,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import LibraryAnalysesComponent from "../library/LibraryAnalysesComponent";
import LibraryPlansComponent from "../library/LibraryPlansComponent";
import LibraryAgentsComponent from "../library/LibraryAgentsComponent";
import DarwinAgentsSelection from "./DarwinAgentsSelection";
import PromptSelectionTable from "./PromptSelectionTable";
import CustomAgentRunsSelection from "./CustomAgentRunsSelection";
import ChatPage from "../../pages/chat/ChatPage";
import { generateSystemPrompt, fetchStepData } from "services/NewDarwinEvaServices";
interface Step {
    id: string;
    title: string;
    description: string;
    component: React.ComponentType<{
        onSelectionChange?: (selectedItems: Set<string>) => void;
        selectedItems?: Set<string>;
        hideActions?: boolean;
        singleSelection?: boolean;
        selectedAgentId?: string;
        selectedCustomAgentId?: string[];
        onValidationChange?: (isValid: boolean) => void;
        onClearSelection?: () => void;
    }>;
}

interface EvaPlaygroundProps {
    initialDarwinAgentId?: string;
    initialPromptId?: string;
    startAtStepIndex?: number; // 0-based; pass 2 to skip first two steps
}

const EvaPlayground: React.FC<EvaPlaygroundProps> = ({
    initialDarwinAgentId,
    initialPromptId,
    startAtStepIndex,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedItems, setSelectedItems] = useState<Record<string, Set<string>>>({});
    const [generatedPrompt, setGeneratedPrompt] = useState("");
    const [sessionId, setSessionId] = useState<string>("");
    const [isGenerating, setIsGenerating] = useState(false);
    const toast = useToast();

    // Get selected custom agents to determine if we need the runs step
    const selectedCustomAgents = selectedItems["custom_agents"] || new Set();
    const hasCustomAgents = selectedCustomAgents.size > 0;

    // Step definitions with library components
    const baseSteps: Step[] = [
        {
            id: "darwin_agents",
            title: "Darwin Agents",
            description: "Select Darwin agents for your analysis",
            component: DarwinAgentsSelection
        },
        {
            id: "prompts",
            title: "Prompts",
            description: "Select prompts for the chosen Darwin agent",
            component: PromptSelectionTable
        },
        {
            id: "analyses",
            title: "Analysis Trees",
            description: "Select the analysis trees you want to work with",
            component: LibraryAnalysesComponent
        },
        {
            id: "plans",
            title: "Analysis Plans",
            description: "Choose the analysis plans to include",
            component: LibraryPlansComponent
        },
        {
            id: "custom_agents",
            title: "Custom Agents",
            description: "Select custom agents for your analysis",
            component: LibraryAgentsComponent
        }
    ];

    // Conditionally add the agent runs step only if custom agents are selected
    const steps: Step[] = hasCustomAgents
        ? [
            ...baseSteps,
            {
                id: "agent_runs",
                title: "Custom Agent Runs",
                description: "Select job runs from the chosen custom agents",
                component: CustomAgentRunsSelection
            }
        ]
        : baseSteps;

    const handleItemSelection = (stepId: string, selectedItems: Set<string>) => {
        setSelectedItems(prev => ({
            ...prev,
            [stepId]: selectedItems
        }));
    };

    const handleClearSelection = (stepId: string) => {
        setSelectedItems(prev => ({
            ...prev,
            [stepId]: new Set()
        }));
    };

    // Initialize preselected agent and prompt and skip steps if provided
    useEffect(() => {
        const preselected: Record<string, Set<string>> = {};
        if (initialDarwinAgentId) {
            preselected["darwin_agents"] = new Set([initialDarwinAgentId]);
        }
        if (initialPromptId) {
            preselected["prompts"] = new Set([initialPromptId]);
        }
        if (Object.keys(preselected).length > 0) {
            setSelectedItems(prev => ({ ...prev, ...preselected }));
        }
        if (typeof startAtStepIndex === "number" && startAtStepIndex >= 0) {
            setCurrentStep(startAtStepIndex);
        } else if (initialDarwinAgentId && initialPromptId) {
            // Default: if both provided, skip first two steps
            setCurrentStep(2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDarwinAgentId, initialPromptId, startAtStepIndex]);

    const handleNext = () => {
        // Validation logic for required selections
        if (currentStep === 0) {
            // Must select exactly 1 Darwin agent
            const selectedAgents = selectedItems["darwin_agents"] || new Set();
            if (selectedAgents.size !== 1) {
                toast({
                    title: "Selection Required",
                    description: "Please select exactly 1 Darwin agent to proceed.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
        } else if (currentStep === 1) {
            // Must select exactly 1 prompt
            const selectedPrompts = selectedItems["prompts"] || new Set();
            if (selectedPrompts.size !== 1) {
                toast({
                    title: "Selection Required",
                    description: "Please select exactly 1 prompt to proceed.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
        }

        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleGenerateTemplate = async () => {
        setIsGenerating(true);

        try {
            // First, fetch data for all selected items
            const stepDataPromises = Object.entries(selectedItems).map(async ([stepId, selectedIds]) => {
                const ids = Array.from(selectedIds);
                if (ids.length === 0) return { stepId, data: [] };

                const data = await fetchStepData(stepId, ids);
                return { stepId, data };
            });

            const stepDataResults = await Promise.all(stepDataPromises);
            const stepDataMap = stepDataResults.reduce((acc, { stepId, data }) => {
                acc[stepId] = data;
                return acc;
            }, {} as Record<string, any[]>);

            // Extract specific data
            const analyses = stepDataMap["analyses"] || [];
            const plans = stepDataMap["plans"] || [];
            const customAgents = stepDataMap["custom_agents"] || [];
            const agentRuns = stepDataMap["agent_runs"] || [];

            // Get the selected Darwin agent and prompt IDs
            const selectedDarwinAgentId = Array.from(selectedItems["darwin_agents"] || new Set())[0];
            const selectedPromptId = Array.from(selectedItems["prompts"] || new Set())[0];

            // Add all selected job runs to the custom agent
            if (customAgents.length > 0 && agentRuns.length > 0) {
                customAgents[0].agent_runs = agentRuns.filter(run => run !== null);
            }

            // Build the request data with proper ChatRequest structure
            const requestData = {
                darwinAgentId: selectedDarwinAgentId,
                promptId: selectedPromptId,
                trees: analyses.filter(analysis => analysis !== null),
                plans: plans.filter(plan => plan !== null),
                customAgents: customAgents.filter(agent => agent !== null)
            };

            const response = await generateSystemPrompt(requestData);

            if (response && response.chat_id) {
                // Store the session_id from the response
                setSessionId(response.chat_id);

                // Extract the generated content from the ChatResponse
                if (response.messages && response.messages.length > 0) {
                    const lastMessage = response.messages[response.messages.length - 1];
                    const generatedContent = lastMessage.content || JSON.stringify(response);
                    setGeneratedPrompt(generatedContent);
                } else {
                    // If no messages, use the entire response as the prompt
                    setGeneratedPrompt(JSON.stringify(response));
                }

                toast({
                    title: "Template Generated",
                    description: "Your agent has been initialized successfully!",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error("No response received from the API");
            }
        } catch (error) {
            console.error("Error generating template:", error);
        } finally {
            setIsGenerating(false);
        }
    };

    // Check if current step is valid for navigation
    const isCurrentStepValid = () => {
        if (currentStep === 0) {
            // Must select exactly 1 Darwin agent
            const selectedAgents = selectedItems["darwin_agents"] || new Set();
            return selectedAgents.size === 1;
        } else if (currentStep === 1) {
            // Must select exactly 1 prompt
            const selectedPrompts = selectedItems["prompts"] || new Set();
            return selectedPrompts.size === 1;
        }
        return true; // Other steps don't have validation requirements
    };


    if (generatedPrompt) {
        return (
            <ChatPage
                hideArtifacts={true}
                systemPrompt={generatedPrompt}
                showSystemPromptButton={true}
                sessionId={sessionId}
                isFromEvaPlayground={true}
            />
        );
    }

    return (
        <VStack align="stretch" pt={4} pb={4}>
            <Heading size="md">Eva Playground</Heading>

            {/* Progress indicator */}
            <Wrap spacing={2} justify="center" align="center">
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <WrapItem>
                            <Badge
                                colorScheme={index <= currentStep ? "blue" : "gray"}
                                variant={index === currentStep ? "solid" : "outline"}
                                px={3}
                                py={1}
                                borderRadius="full"
                            >
                                {index + 1}. {step.title}
                            </Badge>
                        </WrapItem>
                        {index < steps.length - 1 && (
                            <WrapItem>
                                <Text mb={0}>→</Text>
                            </WrapItem>
                        )}
                    </React.Fragment>
                ))}
            </Wrap>

            {/* Step completion status */}
            <Box textAlign="center">
                <Text fontSize="sm" color="gray.600" mb={0}>
                    {Object.values(selectedItems).reduce((total, items) => total + items.size, 0)} items selected across all steps
                </Text>
            </Box>

            {/* Current step content */}
            <Box>
                {/* Render the appropriate library component */}
                {React.createElement(steps[currentStep].component, {
                    onSelectionChange: (selectedItems: Set<string>) =>
                        handleItemSelection(steps[currentStep].id, selectedItems),
                    selectedItems: selectedItems[steps[currentStep].id] || new Set(),
                    hideActions: true,
                    singleSelection: steps[currentStep].id === "custom_agents",
                    selectedAgentId: steps[currentStep].id === "prompts"
                        ? Array.from(selectedItems["darwin_agents"] || new Set())[0]
                        : undefined,
                    selectedCustomAgentId: steps[currentStep].id === "agent_runs"
                        ? Array.from(selectedItems["custom_agents"] || new Set())
                        : undefined,
                    onClearSelection: steps[currentStep].id === "custom_agents"
                        ? () => handleClearSelection(steps[currentStep].id)
                        : undefined,
                })}
            </Box>

            {/* Navigation buttons */}
            <HStack justify="space-between" ml={6} mr={6}>
                <Button
                    leftIcon={<ChevronLeftIcon />}
                    onClick={handlePrevious}
                    isDisabled={currentStep === 0}
                    variant="outline"
                >
                    Previous
                </Button>

                {currentStep === steps.length - 1 ? (
                    <Button
                        colorScheme="blue"
                        onClick={handleGenerateTemplate}
                        isLoading={isGenerating}
                        loadingText="Generating..."
                        rightIcon={isGenerating ? <Spinner size="sm" /> : undefined}
                    >
                        Generate Prompt
                    </Button>
                ) : (
                    <Button
                        rightIcon={<ChevronRightIcon />}
                        onClick={handleNext}
                        colorScheme={isCurrentStepValid() ? "blue" : "gray"}
                        isDisabled={!isCurrentStepValid()}
                    >
                        Next
                    </Button>
                )}
            </HStack>
        </VStack>
    );
};

export default EvaPlayground;
